import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "@/lib/api";
import { Modal } from "@/components/Modal";

interface PlanRow {
  unitId: string;
  unitNumber: string;
  ownerName: string;
  email: string | null;
  action: "create" | "link" | "skip";
  reason?: string;
  credentialsEmailed?: boolean;
  error?: string;
}

interface ApplyResult {
  created: PlanRow[];
  linked: PlanRow[];
  skipped: PlanRow[];
  failed: PlanRow[];
}

/**
 * "Crear usuarios titulares": previews (dry run) which units would get a login,
 * which existing accounts would be linked and which are skipped and why, then
 * applies on confirmation. Credentials go out by email only — nothing is shown here.
 */
export function ProvisionOwnersModal({
  open,
  onClose,
  onDone,
  tr,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  tr: (es: string, en: string) => string;
}) {
  const [plan, setPlan] = useState<PlanRow[] | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: async () => (await api.post<{ plan: PlanRow[] }>("/units/provision-owners", { dryRun: true })).data,
    onSuccess: (data) => setPlan(data.plan),
    onError: (err) => setError((err as AxiosError<{ message: string }>).response?.data?.message ?? "Error"),
  });
  const apply = useMutation({
    mutationFn: async () => (await api.post<ApplyResult>("/units/provision-owners", { dryRun: false })).data,
    onSuccess: (data) => {
      setResult(data);
      onDone();
    },
    onError: (err) => setError((err as AxiosError<{ message: string }>).response?.data?.message ?? "Error"),
  });

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    setResult(null);
    setError(null);
    preview.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const creates = plan?.filter((r) => r.action === "create") ?? [];
  const links = plan?.filter((r) => r.action === "link") ?? [];
  const skips = plan?.filter((r) => r.action === "skip") ?? [];

  const Section = ({ title, rows, tone }: { title: string; rows: PlanRow[]; tone: "green" | "blue" | "slate" | "red" }) => {
    const color = { green: "text-emerald-700", blue: "text-blue-700", slate: "text-slate-600", red: "text-red-700" }[tone];
    return (
      <div>
        <h3 className={`text-sm font-semibold ${color}`}>
          {title} ({rows.length})
        </h3>
        {rows.length === 0 ? (
          <p className="text-xs text-slate-400">—</p>
        ) : (
          <ul className="mt-1 space-y-1 text-xs text-slate-700 max-h-40 overflow-y-auto border border-slate-100 rounded p-2">
            {rows.map((r) => (
              <li key={r.unitId}>
                <span className="font-medium">{r.unitNumber}</span> · {r.ownerName}
                {r.email && <span className="text-slate-500"> · {r.email}</span>}
                {r.reason && <span className="text-slate-500"> — {r.reason}</span>}
                {r.error && <span className="text-red-600"> — {r.error}</span>}
                {r.credentialsEmailed === false && (
                  <span className="text-amber-700"> — {tr("el correo no pudo enviarse", "email could not be sent")}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title={tr("Crear usuarios titulares", "Create owner logins")} size="lg">
      <div className="space-y-4">
        {result ? (
          <>
            <p className="text-sm text-slate-600">
              {tr(
                "Listo. Cada titular nuevo recibió por correo su contraseña temporal. Si algún correo falló, el titular puede usar «¿Olvidó su contraseña?» en la pantalla de acceso.",
                "Done. Each new owner received a temporary password by email. If an email failed, the owner can use “Forgot your password?” on the sign-in screen."
              )}
            </p>
            <Section title={tr("Creados", "Created")} rows={result.created} tone="green" />
            <Section title={tr("Vinculados a su unidad", "Linked to their unit")} rows={result.linked} tone="blue" />
            <Section title={tr("Omitidos", "Skipped")} rows={result.skipped} tone="slate" />
            <Section title={tr("Fallidos", "Failed")} rows={result.failed} tone="red" />
            <div className="flex justify-end pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800">
                {tr("Cerrar", "Close")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              {tr(
                "Se creará un usuario RESIDENTE por cada unidad que tenga correo del titular y aún no tenga usuario. La contraseña temporal se envía solo por correo. Revise la vista previa antes de confirmar.",
                "A RESIDENT login is created for every unit that has an owner email and no login yet. The temporary password is sent by email only. Review the preview before confirming."
              )}
            </p>
            {preview.isPending && <p className="text-sm text-slate-500">{tr("Calculando…", "Calculating…")}</p>}
            {plan && (
              <>
                <Section title={tr("Se crearán", "Will be created")} rows={creates} tone="green" />
                <Section title={tr("Se vincularán (usuario ya existe)", "Will be linked (login exists)")} rows={links} tone="blue" />
                <Section title={tr("Se omitirán", "Will be skipped")} rows={skips} tone="slate" />
              </>
            )}
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border rounded border-slate-300 hover:bg-slate-50">
                {tr("Cancelar", "Cancel")}
              </button>
              <button
                onClick={() => apply.mutate()}
                disabled={!plan || apply.isPending || creates.length + links.length === 0}
                className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-60"
              >
                {apply.isPending
                  ? tr("Creando…", "Creating…")
                  : tr(`Confirmar (${creates.length} nuevos, ${links.length} vínculos)`, `Confirm (${creates.length} new, ${links.length} links)`)}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
